import { useMemo } from "react";
import {
  buildCoachAthleteCard,
  computeWeeklyTargets,
  findPinnedProgram,
  programWeeklyPrescription,
  type AthleteTargets,
  type CoachAthleteCard,
  type Locale,
  type Program,
  type ProgramRow,
} from "@heraion/shared";

import { buildMuscleCoverage } from "../Progress/muscleCoverage";
import { WHOLE_CATALOG } from "../Progress/muscleCoverageProposal";
import { useExerciseNameResolver } from "../Progress/useExerciseNameResolver";
import { useSessionResultsFromDeps } from "../Progress/useSessionResults";
import type { ProgressDeps } from "../Progress/types";

// What <AthleteDetail> derives from the athlete's session-results snapshot:
// the live header card, the coverage census and the weekly targets score.
// Split out of AthleteDetail.tsx (#25) to make room for the sessions section.

export type AthleteDetailDerivedInput = {
  card: CoachAthleteCard;
  deps: ProgressDeps;
  programs: ReadonlyArray<ProgramRow>;
  currentProgramId?: string | undefined;
  weekStartDay?: number | undefined;
  athleteTargets: AthleteTargets | null;
  locale: Locale;
};

export function useAthleteDetailDerived({
  card,
  deps,
  programs,
  currentProgramId,
  weekStartDay,
  athleteTargets,
  locale,
}: AthleteDetailDerivedInput) {
  // ONE fetch pair for every panel below: coverage reads the snapshot here, and
  // every mounted panel reads the same single-flight cache instance.
  // `error` is read, not dropped: the coverage hero is the first thing on
  // this screen now, so a failed read has to say so instead of rendering as
  // an absence (PG-8).
  const { results, catalog, error } = useSessionResultsFromDeps(deps);

  // Only a program the coach may read has sessions to prescribe from: a
  // summary (#332) leaves the panels without the 2nd reference.
  const pinned = useMemo<Program | null>(
    () => findPinnedProgram(programs, currentProgramId).program,
    [programs, currentProgramId],
  );
  const prescription = useMemo(
    () =>
      pinned && catalog ? programWeeklyPrescription(pinned, catalog) : null,
    [pinned, catalog],
  );
  const coverage = useMemo(
    () =>
      results && catalog
        ? buildMuscleCoverage(
            results,
            catalog,
            pinned,
            prescription,
            Date.now(),
            weekStartDay,
            athleteTargets,
            // A place never reaches a coach: the proposal reads the catalog.
            WHOLE_CATALOG,
          )
        : null,
    [results, catalog, pinned, prescription, weekStartDay, athleteTargets],
  );
  // CO-7 / ME-3: the full weekly-targets score (every axis, not the muscle-only
  // coverage census) for the read-only targets panel. Same inputs as coverage,
  // so it rides the SAME session-results snapshot, no extra fetch. The TARGET
  // source is the athlete's own AthleteTargets (host-fetched); the pinned
  // program stays only for the block name + prescription 2nd reference, so the
  // panel no longer needs a pinned program to render.
  const weeklyTargets = useMemo(
    () =>
      results && catalog
        ? computeWeeklyTargets(
            results,
            catalog,
            pinned,
            Date.now(),
            prescription,
            { weekStartDay },
            athleteTargets,
            locale,
          )
        : null,
    [
      pinned,
      results,
      catalog,
      prescription,
      weekStartDay,
      athleteTargets,
      locale,
    ],
  );
  // Header from the same live snapshot as the panels (one as-of); the tap-time
  // `card` only stands in while the read loads.
  const liveCard = useMemo(
    () =>
      results && catalog
        ? buildCoachAthleteCard(
            {
              sub: card.sub,
              name: card.name,
              weekStartDay,
              currentProgramId,
              sessions: results,
              catalog,
              programs,
              athleteTargets,
            },
            Date.now(),
            locale,
          )
        : card,
    [
      card,
      results,
      catalog,
      weekStartDay,
      currentProgramId,
      programs,
      athleteTargets,
      locale,
    ],
  );
  // Resolve exercise-axis row labels BY SLUG against the live catalog (never a
  // write-time snapshot): the shared resolver Progress already uses.
  const resolveExerciseName = useExerciseNameResolver(catalog);

  return {
    results,
    catalog,
    error,
    coverage,
    weeklyTargets,
    liveCard,
    resolveExerciseName,
  };
}
